import React, { useCallback, useEffect } from 'react';
import { View } from 'react-native';
import { Banner, KeyboardScreenContainer, Button, Text } from '~/components';
import * as yup from 'yup';
import { isCpf } from '~/utils/yup';
import { useRoute, type RouteProp } from '@react-navigation/native';
import type { RootStackParamList } from '~/navigation/types';

import StoreSVG from '~/assets/svgs/store.svg';
import { useCheckout, useSalesFunnel } from '~/stores';
import { format, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import useAppNavigation from '~/hooks/useAppNavigation';
import { getPaymentMethods } from '~/utils/insuranceAuto';
import { ContractSellerStepNames } from '~/utils/salesFunnel';

export const schema = yup
  .object()
  .shape({
    document: isCpf(),
    isNotInsured: yup.bool(),
  })
  .required();

const ContractSellerConfirmation: React.FC = () => {
  const { navigate } = useAppNavigation();
  const { insuranceFormData, contractSeller } = useCheckout();
  const { newStepLog } = useSalesFunnel();

  const { params } =
    useRoute<
      RouteProp<RootStackParamList, 'MarisaServicos_ContractSellerConfirmation'>
    >();

  const handleContinue = useCallback(() => {
    navigate('MarisaServicos_Checkout', {
      type: params.type,
      paymentMethods:
        params.type === 'marisa-auto'
          ? getPaymentMethods(insuranceFormData.selectedSimulation)
          : undefined,
    });
  }, [insuranceFormData.selectedSimulation, navigate, params.type]);

  useEffect(() => {
    newStepLog(ContractSellerStepNames.INFO);
  }, [newStepLog]);

  return (
    <View className="flex-1 bg-light-100">
      <KeyboardScreenContainer>
        <View className="flex-1 container mx-auto xl:flex-row xl:pt-12 xl:pb-24">
          <Banner type={params.type} />

          <View className="mt-8 xl:mt-0 px-6 flex-1 xl:ml-24">
            <View className="items-center mb-8">
              <Text className="text-gray-700 text-xl">
                Confirmação dos dados da Loja
              </Text>
            </View>

            <View className="flex-row items-center justify-between mb-4">
              <Text className="text-xs text-gray-600">Solicitado em:</Text>
              <Text className="text-xs text-gray-400">
                {format(parseISO(new Date().toISOString()), 'dd MMM yyyy', {
                  locale: ptBR,
                })}
              </Text>
            </View>

            <View className="flex-row items-center justify-between">
              <Text className="text-xs text-gray-600">Vendedor (a):</Text>
              <Text className="text-xs text-gray-700">
                {contractSeller?.seller?.name ?? 'Vendedor (a) não localizado'}
              </Text>
            </View>
            {!!contractSeller?.seller?.name && (
              <Text className="text-right text-xs text-gray-400">
                CPF. {contractSeller.seller.document}
              </Text>
            )}

            <View className="h-[1px] bg-gray-200 my-8 mx-2" />

            <View className="flex-row items-center mb-4">
              <View className="p-3 bg-secondary-200 rounded-full">
                <StoreSVG width={16} height={16} color={'#585482'} />
              </View>
              <Text className="ml-2 text-base text-gray-700">
                Loja Selecionada
              </Text>
            </View>

            <View className="rounded-lg p-4 border border-primary-600 shadow-lg bg-light-100">
              {contractSeller?.store?.name ? (
                <>
                  <Text className="text-primary-600 font-medium text-xs mb-2 uppercase">
                    {contractSeller?.store.code} - {contractSeller?.store.name}
                  </Text>

                  <Text className="text-gray-400 text-xs">
                    {contractSeller?.store.address}
                  </Text>
                </>
              ) : (
                <Text className="text-primary-600 font-medium text-xs">
                  LOJA NÃO ENCONTRADA
                </Text>
              )}
            </View>

            <View className="flex-1 justify-end">
              <Button className="w-full" onPress={handleContinue}>
                CONTINUAR
              </Button>
            </View>
          </View>
        </View>
      </KeyboardScreenContainer>
    </View>
  );
};
export default ContractSellerConfirmation;
